/**
 * Home page Popular Destinations — precomputed snapshot (fast read path).
 * Regional tab uses regional bundle catalogue from the same rules as GET /bundles?type=regional.
 */

import { apiClient } from '@/shared/services/api/client'
import type { ApiResponse } from '@/shared/types'
import type { ApiBundle } from '@/modules/plans/services/plans.service'

export interface HomeDestinationDto {
  name: string
  iso: string
  startingFromUsd: number | null
  available: boolean
}

export interface HomeGlobalPlanDto {
  id: string
  name: string
  startingFromUsd: number | null
  available: boolean
}

export interface HomeRegionalRegionDto {
  key: string
  label: string
  bundleCount: number
  countryCount: number
  countries: HomeDestinationDto[]
  bundles: ApiBundle[]
}

export interface HomeRegionalCatalogDto {
  regions: HomeRegionalRegionDto[]
}

export interface HomePopularDestinations {
  local: HomeDestinationDto[]
  /** Regional multi-country bundles grouped by catalogue label. */
  regional: HomeRegionalCatalogDto
  /** Nomad global plans only (same as GET /bundles?type=global) */
  global: HomeGlobalPlanDto[]
}

export interface HomeDataMeta {
  builtAt?: string
  stale?: boolean
  snapshotStatus?: string
  buildDurationMs?: number
}

export interface HomeDataResponse {
  popularDestinations: HomePopularDestinations
  meta?: HomeDataMeta
}

const emptyRegionalCatalog = (): HomeRegionalCatalogDto => ({ regions: [] })

class HomeDataService {
  async getHomeData(): Promise<HomeDataResponse> {
    const response = await apiClient.get<ApiResponse<HomeDataResponse>>('/home-data')
    if (!response.data?.popularDestinations) {
      throw new Error('Home data unavailable')
    }
    const pd = response.data.popularDestinations
    return {
      ...response.data,
      popularDestinations: {
        local: pd.local ?? [],
        regional: normalizeRegionalCatalog(pd.regional),
        global: pd.global ?? [],
      },
    }
  }
}

/** Backward-compatible: accept legacy map shape or new catalog shape. */
function normalizeRegionalCatalog(raw: unknown): HomeRegionalCatalogDto {
  if (!raw || typeof raw !== 'object') {
    return emptyRegionalCatalog()
  }
  if ('regions' in raw && Array.isArray((raw as HomeRegionalCatalogDto).regions)) {
    const catalog = raw as HomeRegionalCatalogDto
    return {
      regions: catalog.regions.map(r => ({
        key: r.key ?? '',
        label: r.label ?? r.key ?? '',
        bundleCount: r.bundleCount ?? r.bundles?.length ?? 0,
        countryCount: r.countryCount ?? r.countries?.length ?? 0,
        countries: r.countries ?? [],
        bundles: r.bundles ?? [],
      })),
    }
  }
  return emptyRegionalCatalog()
}

export const homeDataService = new HomeDataService()
